import React, { useState, useEffect } from "react";
import {
    ArrowUpRight,
    Clock,
    CheckCircle,
    AlertCircle,
    Wallet,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    Send,
    CreditCard,
    TrendingUp,
    ArrowLeft,
    Copy,
    Filter,
    Calendar,
    Download,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { API_BASE_URL } from "../../utils/apiConfig";
import { exportTransactionsToExcel } from "../../utils/excelExport";
import Permission from "../Permission";
import usePermissions from "../../hooks/usePermissions";
import { fetchUserWallet, selectAvailableBalance } from "../../store/slices/walletSlice";

const NewPayoutRequest = ({setShowWithdrawModal}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { hasPermission, canPerformAction, isSubUser, userRole } = usePermissions();
    const [amount, setAmount] = useState("");
    const [merchantWallet, setMerchantWallet] = useState("");
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [withdrawals, setWithdrawals] = useState([]);
    const [allWithdrawals, setAllWithdrawals] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const ITEMS_PER_PAGE = 10;
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [currencyType, setCurrencyType] = useState("USDT-TRC20");
    const [globalTotalRequests, setGlobalTotalRequests] = useState(0);
    const [globalTotalApproved, setGlobalTotalApproved] = useState(0);
    const [globalTotalPending, setGlobalTotalPending] = useState(0);
    const [globalTotalFailed, setGlobalTotalFailed] = useState(0);
    const [globalPendingAmount, setGlobalPendingAmount] = useState(0);
    const [globalFailedAmount, setGlobalFailedAmount] = useState(0);
    const [globalTotalRejected, setGlobalTotalRejected] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // OTP state for payout submission (Fix 1.2)
    const [showOTPModal, setShowOTPModal] = useState(false);
    const [otpCode, setOtpCode] = useState("");
    const [otpMessage, setOtpMessage] = useState("");

    // Balance from Redux (Fix 1.7)
    const walletData = useSelector(selectAvailableBalance);
    const availableBalance = walletData?.availableBalance || 0;

    // Fetch wallet balance on mount (Fix 1.7)
    useEffect(() => {
        dispatch(fetchUserWallet());
    }, [dispatch]);

    // Filter states
    const [statusFilter, setStatusFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState("all");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    // Filter withdrawals based on date and status
    const filterWithdrawals = (data) => {
        // Note: Records with type="refund" are mislabeled withdrawal requests
        // (old frontend bug, fixed in commit 5b53c70). Show all records.
        let filtered = [...data];

        // Status filter
        if (statusFilter !== "all") {
            filtered = filtered.filter((w) => w.status === statusFilter);
        }

        // Date filter
        if (dateFilter !== "all") {
            const now = new Date();

            filtered = filtered.filter((w) => {
                const wDate = new Date(w.createdAt);

                switch (dateFilter) {
                    case "today":
                        return wDate.toDateString() === now.toDateString();
                    case "week":
                        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                        return wDate >= weekAgo;
                    case "month":
                        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                        return wDate >= monthAgo;
                    case "custom":
                        if (startDate && endDate) {
                            const start = new Date(startDate);
                            const end = new Date(endDate);
                            end.setHours(23, 59, 59, 999); // Include the entire end date
                            return wDate >= start && wDate <= end;
                        }
                        return true;
                    default:
                        return true;
                }
            });
        }

        return filtered;
    };

    // Apply client-side pagination to filtered data
    const applyPagination = (filteredData) => {
        const startIndex = (page - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        const paginatedData = filteredData.slice(startIndex, endIndex);
        const totalPagesCalculated = Math.ceil(
            filteredData.length / ITEMS_PER_PAGE
        );

        setWithdrawals(paginatedData);
        setTotalPages(totalPagesCalculated);
    };

    // Fetch ALL withdrawals from all pages (Fix 1.4b: safety cap at 10 pages)
    const fetchAllWithdrawals = async () => {
        setLoading(true);
        try {
            let pageNum = 1;
            let allData = [];
            let totalPagesLocal = 1;

            do {
                const response = await fetch(
                    `${API_BASE_URL}/withdrawal/my-withdrawals?page=${pageNum}&limit=50`,
                    {
                        method: "GET",
                        credentials: "include",
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );

                const data = await response.json();

                if (response.ok) {
                    allData = [...allData, ...data.data];
                    totalPagesLocal = data.totalPages;
                    pageNum++;
                } else {
                    setError(data.message);
                    break;
                }
            } while (pageNum <= totalPagesLocal && pageNum <= 10);

            // Store all withdrawals
            setAllWithdrawals(allData);

            // Apply filters and pagination
            const filteredData = filterWithdrawals(allData);
            applyPagination(filteredData);
        } catch (err) {
            setError("An error occurred while fetching withdrawals.");
        } finally {
            setLoading(false);
        }
    };

    // Handle page change for client-side pagination
    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    // Apply filters when filter state changes
    useEffect(() => {
        if (allWithdrawals.length > 0) {
            const filteredData = filterWithdrawals(allWithdrawals);
            setPage(1); // Reset to first page when filters change
            applyPagination(filteredData);
        }
    }, [statusFilter, dateFilter, startDate, endDate]);

    // Apply pagination when page changes
    useEffect(() => {
        if (allWithdrawals.length > 0) {
            const filteredData = filterWithdrawals(allWithdrawals);
            applyPagination(filteredData);
        }
    }, [page]);

    // Initial data fetch
    useEffect(() => {
        fetchAllWithdrawals();
    }, []);

    const fetchAllWithdrawalsForStats = async () => {
        try {
            // Use the already fetched data for stats calculation
            const allData = allWithdrawals.length > 0 ? allWithdrawals : [];

            if (allData.length === 0) return;

            // Filter out refunds and apply current filters for stats
            const filteredData = filterWithdrawals(allData);

            // Filter by status
            const completedItems = filteredData.filter(
                (w) => w.status === "completed"
            );
            const pendingItems = filteredData.filter((w) => w.status === "pending");
            const failedItems = filteredData.filter((w) => w.status === "failed");
            const rejectedItems = filteredData.filter((w) => w.status === "rejected");

            // Aggregate
            setGlobalTotalRequests(filteredData.length);
            setGlobalTotalApproved(
                completedItems.reduce((sum, w) => sum + parseFloat(w.amount || 0), 0)
            );
            setGlobalTotalPending(pendingItems.length);
            setGlobalTotalFailed(failedItems.length);

            setGlobalPendingAmount(
                pendingItems.reduce((sum, w) => sum + parseFloat(w.amount || 0), 0)
            );
            setGlobalFailedAmount(
                failedItems.reduce((sum, w) => sum + parseFloat(w.amount || 0), 0)
            );
            setGlobalTotalRejected(
                rejectedItems.reduce((sum, w) => sum + parseFloat(w.amount || 0), 0)
            );
        } catch (err) {
            console.error("Error calculating stats:", err);
        }
    };

    useEffect(() => {
        fetchAllWithdrawalsForStats();
    }, [allWithdrawals, statusFilter, dateFilter, startDate, endDate]);

    // Handle form submit to initiate withdrawal (Fix 1.2: OTP flow)
    const handleSubmit = async () => {
        setError("");
        setSuccessMessage("");
        setIsSubmitting(true);

        if (!amount || !merchantWallet) {
            setError("Please fill in all fields");
            setIsSubmitting(false);
            return;
        }

        // Balance validation (Fix 1.7)
        if (parseFloat(amount) > availableBalance) {
            setError("Insufficient balance");
            setIsSubmitting(false);
            return;
        }

        try {
            const payload = {
                amount: parseFloat(amount),
                merchantWallet: merchantWallet,
                currencyType: currencyType,
            };

            // Include OTP code if available
            if (otpCode) {
                payload.twoFACode = otpCode;
            }

            const response = await fetch(`${API_BASE_URL}/withdrawal`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.ok) {
                // Check if backend requires 2FA/OTP
                if (data.data?.requires2FA || data.data?.requiresOTP) {
                    setOtpMessage(data.message || "Please enter the verification code sent to your email.");
                    setShowOTPModal(true);
                    setOtpCode("");
                    setIsSubmitting(false);
                    return;
                }

                setSuccessMessage(
                    data.message || "Payout request submitted successfully."
                );
                setShowWithdrawModal(false); // Close modal on success
                setShowOTPModal(false);
                setOtpCode("");
                setOtpMessage("");
                setAmount("");
                setMerchantWallet("");
                fetchAllWithdrawals();
                dispatch(fetchUserWallet()); // Refresh balance
            } else {
                // Check for 2FA required response in error
                if (data.data?.requires2FA || data.data?.requiresOTP) {
                    setOtpMessage(data.message || "Please enter the verification code.");
                    setShowOTPModal(true);
                    setOtpCode("");
                    setIsSubmitting(false);
                    return;
                }

                // Handle invalid 2FA code
                if (data.error === "INVALID_2FA_CODE" || (data.message?.toLowerCase().includes("invalid") && data.message?.toLowerCase().includes("code"))) {
                    setOtpMessage("Invalid code. Please try again.");
                    setOtpCode("");
                    setIsSubmitting(false);
                    return;
                }

                setError(
                    data.message || "Something went wrong while submitting payout."
                );
            }
        } catch (err) {
            setError("An error occurred while processing your request.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "completed":
                return "bg-green-100 text-green-700 border-green-300";
            case "confirmed":
                return "bg-green-100 text-green-700 border-green-300";
            case "pending":
                return "bg-yellow-100 text-yellow-700 border-yellow-300";
            case "failed":
                return "bg-red-100 text-red-700 border-red-300";
            case "rejected":
                return "bg-red-100 text-red-700 border-red-300";
            case "executing":
                return "bg-blue-100 text-blue-700 border-blue-300";
            case "cancelled":
                return "bg-gray-100 text-gray-600 border-gray-300";
            default:
                return "bg-gray-100 text-gray-600 border-gray-300";
        }
    };

    // Refresh data
    const refreshData = () => {
        setIsRefreshing(true);
        fetchAllWithdrawals();
        dispatch(fetchUserWallet());
        setTimeout(() => setIsRefreshing(false), 1500);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    // Export filtered withdrawals to Excel
    const handleExportWithdrawals = () => {
        const filteredData = filterWithdrawals(allWithdrawals);

        if (filteredData.length === 0) {
            alert('No withdrawal requests to export');
            return;
        }

        if (!window.confirm(`This will export ALL ${filteredData.length} withdrawal requests from all pages. Continue?`)) {
            return;
        }

        const filters = {
            status: statusFilter,
            dateFilter: dateFilter,
            startDate: startDate,
            endDate: endDate
        };

        const result = exportTransactionsToExcel(filteredData, 'withdrawal', filters);

        if (result.success) {
            alert(result.message);
        } else {
            alert(result.message);
        }
    };

    return (
        <div className="w-full  min-h-full">
            <div className="w-full max-w-4xl mx-auto p-6">
                {/* Header Section */}



                {/* Success and Error Messages */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                        <span className="text-red-700">{error}</span>
                    </div>
                )}

                {successMessage && (
                    <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="text-green-700">{successMessage}</span>
                    </div>
                )}

                {/* Main Content */}
                <div className="flex justify-center">
                    {/* Payout Form */}
                    <div className="w-full max-w-md  rounded-xl p-5 ">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg mx-auto mb-4">
                                <Send className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">
                                New Payout Request
                            </h3>
                            <p className="text-gray-600">Submit a new payout request to your wallet</p>
                        </div>

                        {/* Available Balance Display (Fix 1.7) */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                            <div className="text-sm text-blue-700">Available Balance</div>
                            <div className="text-xl font-bold text-blue-800">
                                {availableBalance.toFixed(2)} USDT
                            </div>
                        </div>

                        <div className="space-y-6">
                            {/* Fix E: Currency dropdown enabled (removed disabled={true}) */}
                            <div>
                                <label
                                    htmlFor="currencyType"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Currency Type
                                </label>
                                <select
                                    id="currencyType"
                                    value={currencyType}
                                    onChange={(e) => setCurrencyType(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                >
                                    <option value="USDT-TRC20">USDT-TRC20</option>
                                    <option value="USDT-ERC20">USDT-ERC20</option>
                                </select>
                            </div>

                            <div>
                                <label
                                    htmlFor="amount"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Amount (USDT)
                                </label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        id="amount"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        placeholder="Enter amount"
                                        className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                    />
                                    <CreditCard className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                                </div>
                            </div>

                            <div>
                                <label
                                    htmlFor="merchantWallet"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Merchant Wallet Address
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        id="merchantWallet"
                                        value={merchantWallet}
                                        onChange={(e) => setMerchantWallet(e.target.value)}
                                        placeholder="Enter wallet address"
                                        className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                                    />
                                    <Wallet className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                                </div>
                            </div>

                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-300 shadow-lg flex items-center justify-center space-x-2 ${isSubmitting
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-blue-600 hover:bg-blue-700 hover:shadow-blue-500/25"
                                    } text-white`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <RefreshCw className="w-5 h-5 animate-spin" />
                                        <span>Submitting...</span>
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-5 h-5" />
                                        <span>Submit Payout Request</span>
                                    </>
                                )}
                            </button>
                        </div>
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            {/* Total & Completed */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="text-center">
                                    <div className="text-lg font-bold text-gray-800">
                                        {globalTotalRequests}
                                    </div>
                                    <div className="text-xs text-gray-600">Total Requests</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-lg font-bold text-green-600">
                                        {globalTotalApproved.toFixed(2)} USDT
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        Total Completed Amount
                                    </div>
                                </div>
                            </div>

                            {/* Pending */}
                            <div className="grid grid-cols-2 gap-4 mt-4">
                                <div className="text-center">
                                    <div className="text-lg font-bold text-yellow-600">
                                        {globalTotalPending}
                                    </div>
                                    <div className="text-xs text-gray-600">Pending Requests</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-lg font-bold text-yellow-600">
                                        {globalPendingAmount.toFixed(2)} USDT
                                    </div>
                                    <div className="text-xs text-gray-600">Pending Amount</div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* OTP Modal for payout submission (Fix 1.2) */}
            {showOTPModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            Verification Required
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            {otpMessage || "Please enter the verification code to complete your payout request."}
                        </p>
                        <input
                            type="text"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            placeholder="Enter 6-digit code"
                            maxLength={6}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-widest font-mono mb-4"
                            autoFocus
                        />
                        <div className="flex space-x-3">
                            <button
                                onClick={() => {
                                    setShowOTPModal(false);
                                    setOtpCode("");
                                    setOtpMessage("");
                                }}
                                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={otpCode.length < 6 || isSubmitting}
                                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? "Verifying..." : "Verify & Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewPayoutRequest;
